/**
 * URL Builder Utilities
 *
 * Centralized URL construction for consistent routing across the app.
 * All navigation links and URL construction should use these functions.
 */

// ============================================================================
// Validation Utilities
// ============================================================================

/**
 * Validate global ID format
 * @param globalId - Global ID string to validate
 * @returns true if valid format, false otherwise
 */
export function isValidGlobalId(globalId: string): boolean {
  if (typeof globalId !== 'string') return false;
  if (globalId.length < 4) return false;
  if (globalId.length % 2 !== 0) return false;

  const pattern = /^[a-z]{2}([0-9][a-z])+$/;
  return pattern.test(globalId);
}

/**
 * Extract global ID type prefix
 * @param globalId - Global ID string
 * @returns Type prefix (e.g., "co", "ph", "us") or null if invalid
 */
export function getGlobalIdType(globalId: string): string | null {
  if (!isValidGlobalId(globalId)) return null;
  return globalId.slice(0, 2);
}

/**
 * Validate global ID has expected type prefix
 * @param globalId - Global ID to validate
 * @param expectedPrefix - Expected prefix (e.g., "co", "ph", "us")
 * @throws Error if validation fails
 */
function validateGlobalIdType(globalId: string, expectedPrefix: string, entityType: string): void {
  if (!isValidGlobalId(globalId)) {
    throw new Error(`Invalid ${entityType} global ID format: "${globalId}"`);
  }
  const prefix = getGlobalIdType(globalId);
  if (prefix !== expectedPrefix) {
    throw new Error(`Invalid ${entityType} global ID: expected prefix "${expectedPrefix}", got "${prefix}" in "${globalId}"`);
  }
}

// ============================================================================
// Collection URLs
// ============================================================================

/**
 * Build collection view URL
 * @param collectionId - Collection global ID (e.g., "co4m")
 * @param slug - Optional decorative slug (e.g., "sunset-series")
 * @returns URL path (e.g., "/c/co4m" or "/c/co4m/sunset-series")
 * @throws Error if collectionId is not a valid collection ID
 */
export function collectionUrl(collectionId: string, slug?: string): string {
  validateGlobalIdType(collectionId, 'co', 'collection');
  return slug ? `/c/${collectionId}/${slug}` : `/c/${collectionId}`;
}

/**
 * Build collection edit URL
 * @param collectionId - Collection global ID
 * @param slug - Decorative slug (required for sub-paths)
 * @returns URL path (e.g., "/c/co4m/sunset-series/edit")
 * @throws Error if collectionId is not a valid collection ID
 */
export function collectionEditUrl(collectionId: string, slug: string): string {
  validateGlobalIdType(collectionId, 'co', 'collection');
  return `/c/${collectionId}/${slug}/edit`;
}

/**
 * Build discussion URL (formerly critique)
 * @param sessionId - Discussion global ID (e.g., "cs4m")
 * @param slug - Optional decorative slug
 * @returns URL path (e.g., "/disc/cs4m" or "/disc/cs4m/sunset-series")
 * @throws Error if sessionId is not a valid session ID
 */
export function critiqueUrl(sessionId: string, slug?: string): string {
  validateGlobalIdType(sessionId, 'cs', 'critique session');
  return slug ? `/disc/${sessionId}/${slug}` : `/disc/${sessionId}`;
}

/**
 * Build discussion URL (formerly critique)
 * @param sessionId - Discussion global ID (e.g., "cs4m")
 * @param slug - Optional decorative slug for readability
 * @returns URL path (e.g., "/disc/cs4m" or "/disc/cs4m/sunset-series")
 * @throws Error if sessionId is not a valid critique session ID
 */
export function critiqueSessionUrl(sessionId: string, slug?: string): string {
  validateGlobalIdType(sessionId, 'cs', 'critique session');
  return slug ? `/disc/${sessionId}/${slug}` : `/disc/${sessionId}`;
}

// ============================================================================
// Photo URLs
// ============================================================================

/**
 * Build standalone photo detail URL
 * @param photoId - Photo global ID (e.g., "ph5x")
 * @returns URL path (e.g., "/p/ph5x")
 * @throws Error if photoId is not a valid photo ID
 */
export function photoUrl(photoId: string): string {
  validateGlobalIdType(photoId, 'ph', 'photo');
  return `/p/${photoId}`;
}

/**
 * Build user-scoped photo detail URL
 * @param username - Username slug (e.g., "johndoe")
 * @param photoId - Photo global ID
 * @returns URL path (e.g., "/u/johndoe/photos/ph5x")
 * @throws Error if photoId is not a valid photo ID
 */
export function userPhotoUrl(username: string, photoId: string): string {
  validateGlobalIdType(photoId, 'ph', 'photo');
  return `/u/${username}/photos/${photoId}`;
}

// ============================================================================
// User URLs
// ============================================================================

/**
 * Build user photos page URL
 * @param username - Username slug
 * @returns URL path (e.g., "/u/johndoe/photos")
 */
export function userPhotosUrl(username: string): string {
  return `/u/${username}/photos`;
}

/**
 * Build user collections page URL
 * @param username - Username slug
 * @returns URL path (e.g., "/u/johndoe/collections")
 */
export function userCollectionsUrl(username: string): string {
  return `/u/${username}/collections`;
}

/**
 * Build user profile URL
 * @param username - Username slug
 * @returns URL path (e.g., "/u/johndoe")
 */
export function userProfileUrl(username: string): string {
  return `/u/${username}`;
}

// ============================================================================
// Object Inspector URLs
// ============================================================================

/**
 * Build object inspector detail URL
 * @param globalId - Global ID string (e.g., "us1a", "ph2s", "co4m")
 * @returns URL path (e.g., "/admin/obj/us1a")
 * @throws Error if globalId is not a valid global ID format
 */
export function objUrl(globalId: string): string {
  if (!isValidGlobalId(globalId)) {
    throw new Error(`Invalid global ID format: "${globalId}"`);
  }
  return `/admin/obj/${globalId}`;
}

// ============================================================================
// Admin URLs
// ============================================================================

/**
 * Build admin users list URL
 * @returns URL path ("/admin/users")
 */
export function adminUsersUrl(): string {
  return '/admin/users';
}

/**
 * Build admin user detail URL
 * @param globalId - User global ID (e.g., "us1a")
 * @returns URL path (e.g., "/admin/users/us1a")
 * @throws Error if globalId is not a valid user ID
 */
export function adminUserDetailUrl(globalId: string): string {
  validateGlobalIdType(globalId, 'us', 'user');
  return `/admin/users/${globalId}`;
}

/**
 * Build create test user URL
 * @param preset - Optional preset name to pre-select
 * @returns URL path (e.g., "/admin/users/create-test" or "/admin/users/create-test?preset=photographer")
 */
export function adminCreateTestUserUrl(preset?: string): string {
  const base = '/admin/users/create-test';
  if (preset) {
    return `${base}?preset=${encodeURIComponent(preset)}`;
  }
  return base;
}

/**
 * Build admin user relationships URL
 * Feature: 080-critique-sharing
 * @param globalId - User global ID (e.g., "us1a")
 * @returns URL path (e.g., "/admin/users/us1a/relationships")
 * @throws Error if globalId is not a valid user ID
 */
export function adminUserRelationshipsUrl(globalId: string): string {
  validateGlobalIdType(globalId, 'us', 'user');
  return `/admin/users/${globalId}/relationships`;
}

// ============================================================================
// Admin AI Personas URLs (Feature: 071-ai-critique-sessions)
// ============================================================================

/**
 * Build admin AI personas list URL
 * @returns URL path ("/admin/ai-personas")
 */
export function adminAIPersonasUrl(): string {
  return '/admin/ai-personas';
}

/**
 * Build admin AI persona detail URL
 * @param globalId - AI persona user global ID (e.g., "us1a")
 * @returns URL path (e.g., "/admin/ai-personas/us1a")
 * @throws Error if globalId is not a valid user ID
 */
export function adminAIPersonaDetailUrl(globalId: string): string {
  validateGlobalIdType(globalId, 'us', 'user');
  return `/admin/ai-personas/${globalId}`;
}

/**
 * Build admin AI persona edit URL
 * @param globalId - AI persona user global ID (e.g., "us1a")
 * @returns URL path (e.g., "/admin/ai-personas/us1a/edit")
 * @throws Error if globalId is not a valid user ID
 */
export function adminAIPersonaEditUrl(globalId: string): string {
  validateGlobalIdType(globalId, 'us', 'user');
  return `/admin/ai-personas/${globalId}/edit`;
}

/**
 * Build admin create AI persona URL
 * @returns URL path ("/admin/ai-personas/create")
 */
export function adminCreateAIPersonaUrl(): string {
  return '/admin/ai-personas/create';
}

// ============================================================================
// Auth URLs (Feature: 075-link-based-impersonation, 076-unified-login-tokens)
// ============================================================================

/**
 * Build impersonation auth URL
 * @param nonce - 64-character hex nonce from impersonation link
 * @returns URL path (e.g., "/auth/impersonate?token=abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function impersonateAuthUrl(nonce: string): string {
  if (!/^[0-9a-f]{64}$/i.test(nonce)) {
    throw new Error(`Invalid impersonation nonce format: "${nonce}"`);
  }
  return `/auth/impersonate?token=${nonce}`;
}

/**
 * Build password reset with PIN URL
 * @param nonce - 64-character hex nonce from password reset email
 * @returns URL path (e.g., "/reset-password?token=abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function resetPasswordUrl(nonce: string): string {
  if (!/^[0-9a-f]{64}$/i.test(nonce)) {
    throw new Error(`Invalid reset token format: "${nonce}"`);
  }
  return `/reset-password?token=${nonce}`;
}

/**
 * Build login link URL (passwordless login)
 * @param nonce - 64-character hex nonce from login link email
 * @returns URL path (e.g., "/auth/login-link?token=abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function loginLinkUrl(nonce: string): string {
  if (!/^[0-9a-f]{64}$/i.test(nonce)) {
    throw new Error(`Invalid login token format: "${nonce}"`);
  }
  return `/auth/login-link?token=${nonce}`;
}

// ============================================================================
// Admin Invites URLs (Feature: 647-invite-registration)
// ============================================================================

/**
 * Build admin invites list URL
 * @returns URL path ("/admin/invites")
 */
export function adminInvitesUrl(): string {
  return '/admin/invites';
}

/**
 * Build invite registration URL
 * @param nonce - 64-character hex nonce from invite token
 * @returns URL path (e.g., "/register?invite=abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function inviteRegisterUrl(nonce: string): string {
  if (!/^[0-9a-f]{64}$/i.test(nonce)) {
    throw new Error(`Invalid invite token format: "${nonce}"`);
  }
  return `/register?invite=${nonce}`;
}

// ============================================================================
// Task URLs (Feature: 078-user-tasks)
// ============================================================================

/**
 * Build tasks list URL
 * @returns URL path ("/tasks")
 */
export function tasksUrl(): string {
  return '/tasks';
}

/**
 * Build task detail URL (for debug/inspection)
 * @param taskId - Task global ID (e.g., "ta4m")
 * @returns URL path (e.g., "/tasks/ta4m")
 * @throws Error if taskId is not a valid task ID
 */
export function taskUrl(taskId: string): string {
  validateGlobalIdType(taskId, 'ta', 'task');
  return `/tasks/${taskId}`;
}

// ============================================================================
// Share URLs (Feature: 080-critique-sharing)
// ============================================================================

/**
 * Build critique share URL
 * @param nonce - 64-character hex nonce from share token
 * @returns URL path (e.g., "/share/abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function critiqueShareUrl(nonce: string): string {
  if (!/^[0-9a-f]{64}$/i.test(nonce)) {
    throw new Error(`Invalid share token format: "${nonce}"`);
  }
  return `/share/${nonce}`;
}

/**
 * Build full critique share URL including domain
 * @param nonce - 64-character hex nonce from share token
 * @returns Full URL (e.g., "https://www.206mp.com/share/abc123...")
 * @throws Error if nonce is not a valid 64-character hex string
 */
export function critiqueShareFullUrl(nonce: string): string {
  const path = critiqueShareUrl(nonce);
  // In browser, use current origin
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  // Fallback for SSR/tests
  return path;
}

// ============================================================================
// Return URL Validation (Feature: 892-invite-redirect)
// ============================================================================

/**
 * Validates and decodes a returnUrl parameter.
 * Prevents open redirect attacks by only allowing relative paths.
 *
 * Security requirements:
 * - Must be a relative path (starts with `/`)
 * - Cannot be protocol-relative (blocks `//evil.com`)
 * - Cannot contain `:` before first `/` after start (blocks `http://`, `javascript:`)
 * - Must decode before validation (prevents encoding bypass attacks)
 *
 * @param url - URL-encoded return URL from query params
 * @returns Decoded URL if valid, null if invalid
 */
export function validateReturnUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    const decoded = decodeURIComponent(url);

    // Must start with `/` (relative path)
    if (!decoded.startsWith('/')) {
      return null;
    }

    // Cannot be protocol-relative (e.g., `//evil.com`)
    if (decoded.startsWith('//')) {
      return null;
    }

    // Check for protocol in the URL (e.g., `javascript:`, `data:`)
    // Look for colon before the first slash after position 0
    const colonIndex = decoded.indexOf(':');
    const slashIndex = decoded.indexOf('/', 1); // Start after initial `/`

    if (colonIndex !== -1) {
      // If there's a colon and it comes before any internal slash, reject
      if (slashIndex === -1 || colonIndex < slashIndex) {
        return null;
      }
    }

    return decoded;
  } catch {
    // Invalid URL encoding
    return null;
  }
}
